import * as Sentry from '@sentry/browser'

import type { GridRenderContext } from '../facade/js/liveview'

/**
 * Sentry performance reporter for grid render timing.
 *
 * Emits a `grid.render` span with the measured duration from the timing seam.
 * Reads tenant, user, page, and component from `window.SENTRY` and
 * `window.location` — matching the existing Sentry tag convention for grids.
 *
 * No-op when:
 * - not in the production environment, or
 * - the Sentry client has not been initialized.
 *
 * Does NOT call Sentry.init, override sample rates, or emit on every dev mount.
 */
export const sentryGridRenderReporter = (
  durationMs: number,
  context: GridRenderContext
): void => {
  if (process.env.NODE_ENV !== 'production') {
    return
  }

  if (Sentry.getClient() === undefined) {
    return
  }

  const sentry = (
    window as Window & { SENTRY?: { company_id?: string; account_id?: string } }
  ).SENTRY

  // The duration lives on THIS span's attributes, not on a transaction-global
  // Sentry.setMeasurement: a page can render several grids (tabs, side-by-side),
  // and a transaction-level measurement would let the last grid overwrite the
  // others. A per-span attribute keeps each grid's duration distinct alongside
  // its own tenant/page/component dimensions.
  const endTime = (performance.timeOrigin + performance.now()) / 1000
  const span = Sentry.startInactiveSpan({
    name: 'grid.render',
    op: 'ui.render',
    startTime: endTime - durationMs / 1000,
    attributes: {
      'grid.render.duration_ms': durationMs,
      'grid.tenant': sentry?.company_id,
      'grid.user': sentry?.account_id,
      'grid.page': window.location.pathname,
      'grid.component': context.component,
      'grid.column_count': context.columnCount,
      'grid.row_model': context.rowModelType
    }
  })

  span.end(endTime)
}
